import { PaletteMode } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { paletteBaseTokens } from './paletteTokens';

export interface SemanticColorTokens {
  palette: {
    primary: { main: string; dark: string; light: string; contrastText: string };
    secondary: { main: string; dark: string; light: string; contrastText: string };
    success: { main: string; dark: string; contrastText: string };
    error: { main: string; dark: string; contrastText: string };
    warning: { main: string; dark: string; contrastText: string };
    info: { main: string; dark: string; contrastText: string };
    text: { primary: string; secondary: string; disabled: string };
    background: { default: string; paper: string };
    divider: string;
  };
  surfaces: {
    pageBackdrop: string;
    cardGradient: string;
    dialogGradient: string;
    panelGradient: string;
    dialogActionsBackground: string;
    dataGridRowEven: string;
    dataGridHeaderGradient: string;
    dataGridTotal: string;
    snackbarSurface: (accent: string) => string;
  };
  overlays: {
    selection: string;
    scrollbarThumb: string;
    scrollbarThumbHover: string;
    dialogBackdrop: string;
    dialogHeaderHighlight: string;
  };
}

export const getSemanticColorTokens = (mode: PaletteMode): SemanticColorTokens => {
  const isDark = mode === 'dark';

  return {
    palette: {
      primary: {
        main: paletteBaseTokens.brand.primary[500],
        dark: paletteBaseTokens.brand.primary[700],
        light: alpha(paletteBaseTokens.brand.primary[500], 0.18),
        contrastText: paletteBaseTokens.neutral[0],
      },
      secondary: {
        main: paletteBaseTokens.brand.secondary[500],
        dark: paletteBaseTokens.brand.secondary[700],
        light: alpha(paletteBaseTokens.brand.secondary[500], 0.2),
        contrastText: paletteBaseTokens.neutral[900],
      },
      success: {
        main: paletteBaseTokens.success[500],
        dark: paletteBaseTokens.success[700],
        contrastText: paletteBaseTokens.neutral[0],
      },
      error: {
        main: paletteBaseTokens.error[500],
        dark: paletteBaseTokens.error[700],
        contrastText: paletteBaseTokens.neutral[0],
      },
      warning: {
        main: paletteBaseTokens.warning[500],
        dark: paletteBaseTokens.warning[700],
        contrastText: paletteBaseTokens.neutral[900],
      },
      info: {
        main: paletteBaseTokens.info[500],
        dark: paletteBaseTokens.info[700],
        contrastText: paletteBaseTokens.neutral[0],
      },
      text: {
        primary: isDark ? paletteBaseTokens.neutral[0] : paletteBaseTokens.neutral[900],
        secondary: isDark
          ? alpha(paletteBaseTokens.neutral[0], 0.76)
          : alpha(paletteBaseTokens.neutral[900], 0.72),
        disabled: isDark
          ? alpha(paletteBaseTokens.neutral[0], 0.46)
          : alpha(paletteBaseTokens.neutral[900], 0.42),
      },
      background: {
        default: isDark
          ? paletteBaseTokens.surfaces.darkBackground
          : paletteBaseTokens.neutral[100],
        paper: isDark ? paletteBaseTokens.surfaces.darkPaper : paletteBaseTokens.neutral[0],
      },
      divider: isDark
        ? alpha(paletteBaseTokens.neutral[0], 0.1)
        : alpha(paletteBaseTokens.neutral[900], 0.1),
    },
    surfaces: {
      pageBackdrop: isDark
        ? `radial-gradient(circle at top left, ${alpha(
            paletteBaseTokens.brand.primary[500],
            0.1
          )}, transparent 32rem), radial-gradient(circle at top right, ${alpha(
            paletteBaseTokens.brand.secondary[500],
            0.12
          )}, transparent 30rem)`
        : `radial-gradient(circle at top left, ${alpha(
            paletteBaseTokens.brand.primary[500],
            0.16
          )}, transparent 32rem), radial-gradient(circle at top right, ${alpha(
            paletteBaseTokens.brand.secondary[500],
            0.1
          )}, transparent 30rem)`,
      cardGradient: isDark
        ? `linear-gradient(145deg, ${alpha(paletteBaseTokens.neutral[0], 0.06)}, ${alpha(
            paletteBaseTokens.neutral[0],
            0.02
          )})`
        : `linear-gradient(145deg, ${paletteBaseTokens.neutral[0]}, ${alpha(
            paletteBaseTokens.brand.primary[500],
            0.05
          )})`,
      dialogGradient: isDark
        ? `linear-gradient(145deg, ${paletteBaseTokens.surfaces.darkPaper}, ${alpha(
            paletteBaseTokens.neutral[1000],
            0.2
          )})`
        : `linear-gradient(145deg, ${paletteBaseTokens.neutral[0]}, ${alpha(
            paletteBaseTokens.brand.primary[100],
            0.75
          )})`,
      panelGradient: isDark
        ? `linear-gradient(135deg, ${alpha(paletteBaseTokens.neutral[0], 0.08)}, ${alpha(
            paletteBaseTokens.neutral[0],
            0.03
          )})`
        : `linear-gradient(135deg, ${alpha(paletteBaseTokens.brand.primary[500], 0.1)}, ${alpha(
            paletteBaseTokens.brand.secondary[500],
            0.18
          )})`,
      dialogActionsBackground: isDark
        ? paletteBaseTokens.surfaces.darkDialogActions
        : paletteBaseTokens.neutral[50],
      dataGridRowEven: isDark
        ? alpha(paletteBaseTokens.neutral[0], 0.018)
        : alpha(paletteBaseTokens.neutral[900], 0.018),
      dataGridHeaderGradient: isDark
        ? `linear-gradient(180deg, ${alpha(paletteBaseTokens.neutral[0], 0.08)}, ${alpha(
            paletteBaseTokens.neutral[0],
            0.04
          )})`
        : `linear-gradient(180deg, ${paletteBaseTokens.neutral[0]}, ${alpha(
            paletteBaseTokens.brand.primary[500],
            0.04
          )})`,
      dataGridTotal: isDark
        ? alpha(paletteBaseTokens.neutral[0], 0.05)
        : alpha(paletteBaseTokens.brand.primary[500], 0.12),
      snackbarSurface: () =>
        isDark ? paletteBaseTokens.surfaces.darkPaper : paletteBaseTokens.neutral[0],
    },
    overlays: {
      selection: isDark
        ? alpha(paletteBaseTokens.brand.primary[500], 0.24)
        : alpha(paletteBaseTokens.brand.primary[500], 0.18),
      scrollbarThumb: isDark
        ? alpha(paletteBaseTokens.brand.primary[500], 0.34)
        : alpha(paletteBaseTokens.brand.primary[300], 0.48),
      scrollbarThumbHover: isDark
        ? alpha(paletteBaseTokens.brand.primary[500], 0.52)
        : alpha(paletteBaseTokens.brand.primary[300], 0.56),
      dialogBackdrop: isDark
        ? alpha(paletteBaseTokens.neutral[1000], 0.76)
        : alpha(paletteBaseTokens.neutral[800], 0.42),
      dialogHeaderHighlight: alpha(paletteBaseTokens.brand.primary[500], 0.34),
    },
  };
};
